import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { DEVICE_STORAGE_KEY } from '@/utils/constants';
import { timeoutPromise } from '@/utils/promise';

type State = {
  hydrated: boolean;
  fcmToken: string;
  isPrivate: boolean | undefined;
  isNotificationPermissionRequested: boolean;
  compatibilityCheckedAt: number;
  requireRestart: boolean;
};

type Action = {
  setHydrated(): void;
  clear(): void;
};

export const useDeviceStore = create<State & Action>()(
  persist(
    (set) => ({
      hydrated: false,
      fcmToken: '',
      isPrivate: undefined,
      isNotificationPermissionRequested: false,
      compatibilityCheckedAt: 0,
      requireRestart: false,
      setHydrated() {
        set({ hydrated: true });
      },
      clear() {
        set({ fcmToken: '' });
      },
    }),
    {
      name: DEVICE_STORAGE_KEY,
      version: 5,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        fcmToken: state.fcmToken,
        isNotificationPermissionRequested: state.isNotificationPermissionRequested,
        compatibilityCheckedAt: state.compatibilityCheckedAt,
        isPrivate: state.isPrivate,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      migrate: () => ({
        fcmToken: '',
        isNotificationPermissionRequested: false,
        compatibilityCheckedAt: 0,
      }),
    },
  ),
);

export const setFcmDeviceToken = (token: string) => useDeviceStore.setState(() => ({ fcmToken: token }));
export const requestRestart = (value = true) => useDeviceStore.setState(() => ({ requireRestart: value }));
export const setIsPrivate = (isPrivate: boolean) => useDeviceStore.setState(() => ({ isPrivate }));
export const setNotificationPermissionRequested = (toggle: true) =>
  useDeviceStore.setState(() => ({ isNotificationPermissionRequested: toggle }));
export const setCompatibilityChecked = () => useDeviceStore.setState(() => ({ compatibilityCheckedAt: Date.now() }));

export const getCompatibilityChecked = () =>
  timeoutPromise(
    () =>
      new Promise<boolean>((resolve) => {
        if (useDeviceStore.getState().hydrated) {
          const { compatibilityCheckedAt } = useDeviceStore.getState();
          const isChecked = compatibilityCheckedAt !== 0 && compatibilityCheckedAt < Date.now() + 86_400_000;
          resolve(isChecked);
        } else {
          const unsubscribe = useDeviceStore.subscribe(({ hydrated, compatibilityCheckedAt }) => {
            if (hydrated) {
              const isChecked = compatibilityCheckedAt !== 0 && compatibilityCheckedAt < Date.now() + 86_400_000;
              resolve(isChecked);
              unsubscribe();
            }
          });
        }
      }),
    1000,
    () => false,
  );
